import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useAuth } from '../../hooks/useAuth';
import { authAPI } from '../../api/auth';
import { providersAPI } from '../../api/providers';
import { paymentsAPI } from '../../api/payments';
import PhoneAccountHint from '../../components/common/PhoneAccountHint';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Textarea from '../../components/common/Textarea';
import MapPicker from '../../components/common/MapPicker';
import { resolvePostAuthDestination } from '../../utils/authRedirect';
import {
  BuildingStorefrontIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const STEPS = [
  { id: 1, title: 'Account' },
  { id: 2, title: 'Business' },
  { id: 3, title: 'Location' },
  { id: 4, title: 'Settings' },
  { id: 5, title: 'Plan & Payment' },
];

// Must match the plans page and the backend PLAN_PRICES
const PLAN_INFO = {
  ORDINARY: { label: 'Ordinary', price: 20000, note: 'Up to 3 services' },
  ADVANCED: { label: 'Advanced', price: 30000, note: 'Up to 10 services + portfolio' },
  PREMIUM: { label: 'Premium', price: 80000, note: 'Unlimited + verified badge' },
};

const LANGUAGES = [
  { id: 'en', name: 'English' },
  { id: 'sw', name: 'Swahili' },
  { id: 'lg', name: 'Luganda' },
  { id: 'xog', name: 'Lusoga' },
  { id: 'nyn', name: 'Runyankole' },
  { id: 'cgg', name: 'Rukiga' },
  { id: 'nyo', name: 'Runyoro' },
  { id: 'ttj', name: 'Rutooro' },
  { id: 'ach', name: 'Acholi' },
  { id: 'laj', name: 'Lango' },
  { id: 'teo', name: 'Ateso' },
  { id: 'lgg', name: 'Lugbara' },
  { id: 'myx', name: 'Lumasaba' },
  { id: 'kdj', name: 'Karamojong' },
];

const GENDER_OPTIONS = [
  { value: 'female', label: 'Female Clients Only' },
  { value: 'male', label: 'Male Clients Only' },
  { value: 'all', label: 'All Genders' },
];

const SPECIALIZATIONS = [
  { id: 'hair', name: 'Hair Styling' },
  { id: 'nails', name: 'Nail Care' },
  { id: 'makeup', name: 'Makeup Artistry' },
  { id: 'facial', name: 'Facial Treatments' },
  { id: 'massage', name: 'Massage Therapy' },
  { id: 'waxing', name: 'Waxing/Depilation' },
  { id: 'pedicure', name: 'Pedicure' },
  { id: 'barber', name: 'Barbering' },
];

const getCategoryDescription = (categories, categoryId) => {
  const selected = categories.find((cat) => String(cat.id) === String(categoryId));
  return selected?.description || selected?.desc || '';
};

const GoogleOnboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeGoogleOnboarding } = useAuth();
  const onboardingToken = location.state?.onboardingToken;
  const from = location.state?.from || '/';
  const profile = location.state?.profile || {};
  const googlePicture = profile.picture || profile.avatarUrl || profile.photoUrl || '';

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [accountType, setAccountType] = useState('client');
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    phone: '',
    businessName: '',
    category: '',
    description: '',
    specializations: [],
    latitude: null,
    longitude: null,
    shopAddress: '',
    businessLocation: '',
    offersMobile: false,
    maxTravelKm: 10,
    languages: ['en'],
    genderPreference: 'all',
    hygieneNotes: '',
    autoAcceptBookings: false,
    bufferTimeMinutes: 15,
  });
  const [errors, setErrors] = useState({});

  // Plan & payment — provider accounts require the plan fee, same as email signup
  const [selectedPlan, setSelectedPlan] = useState('ORDINARY');
  const [paymentMethod, setPaymentMethod] = useState('mtn');
  const [paymentPhone, setPaymentPhone] = useState('');
  const [paymentPhoneError, setPaymentPhoneError] = useState('');
  const [feeStatus, setFeeStatus] = useState(null);
  const [feePolling, setFeePolling] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    providersAPI.getCategories()
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Prefill the payment number with the account number at the payment step
  useEffect(() => {
    if (currentStep === 5 && !paymentPhone && formData.phone) {
      setPaymentPhone(formData.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setFeePolling(false);
  };

  const handlePayFee = async () => {
    const isCard = paymentMethod === 'card';
    if (!isCard && (!paymentPhone || !isValidPhoneNumber(paymentPhone))) {
      setPaymentPhoneError('Enter a valid mobile money number to pay with');
      return;
    }
    setPaymentPhoneError('');
    setLoading(true);
    // Optimistic: show the waiting panel immediately; revert on failure
    setFeeStatus('PENDING');
    try {
      const result = await paymentsAPI.initiateRegistrationFee({
        email: profile.email,
        phone: isCard ? undefined : paymentPhone,
        method: paymentMethod,
        plan: selectedPlan,
      });
      setFeeStatus(result.status);
      if (result.status === 'COMPLETED') return;

      if (result.checkoutUrl) {
        setCheckoutUrl(result.checkoutUrl);
        window.open(result.checkoutUrl, '_blank', 'noopener,noreferrer');
      }

      setFeePolling(true);
      let attempts = 0;
      const MAX_ATTEMPTS = isCard ? 240 : 72;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const status = await paymentsAPI.getRegistrationFeeStatus(profile.email);
          setFeeStatus(status.status);
          if (status.status === 'COMPLETED' || status.status === 'FAILED') {
            stopPolling();
            if (status.status === 'FAILED') toast.error('Payment failed. Please try again.');
            return;
          }
        } catch (err) {
          if (err?.response?.status === 404) { stopPolling(); return; }
        }
        if (attempts >= MAX_ATTEMPTS) {
          stopPolling();
          toast('Payment is taking longer than usual. Complete it, then tap "Check payment status".', { icon: '⏳' });
        }
      }, 2500);
    } catch (err) {
      setFeeStatus(null); // revert the optimistic waiting panel
      const errCode = err?.response?.data?.error;
      const errMsg = err?.response?.data?.message;
      if (errCode === 'INVALID_PHONE' || errCode === 'PAYMENT_INIT_FAILED') {
        toast.error(errMsg || 'That number is not registered for mobile money.');
      } else if (typeof errCode === 'string' && errCode.includes('already exists')) {
        toast.error('An account already exists for this email. Please sign in.');
      } else {
        toast.error('Could not initiate payment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkFeeStatusOnce = async () => {
    setLoading(true);
    try {
      const status = await paymentsAPI.getRegistrationFeeStatus(profile.email);
      setFeeStatus(status.status);
      if (status.status === 'FAILED') toast.error('Payment failed. Please try again.');
      else if (status.status !== 'COMPLETED') toast('Still processing — give it a moment and check again.', { icon: '⏳' });
    } catch (_) { /* keep current state */ }
    finally { setLoading(false); }
  };

  if (!onboardingToken) {
    return <Navigate to="/login" replace />;
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'category') {
      setFormData((prev) => ({
        ...prev,
        category: value,
        description: getCategoryDescription(categories, value),
      }));
      if (errors.category || errors.description) {
        setErrors((prev) => ({ ...prev, category: '', description: '' }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleLocationSelect = (lat, lng, address) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      shopAddress: address || prev.shopAddress,
      businessLocation: address || prev.businessLocation,
    }));
    if (errors.mapPicker) setErrors((prev) => ({ ...prev, mapPicker: '' }));
  };

  const toggleSpecialization = (id) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.includes(id)
        ? prev.specializations.filter((s) => s !== id)
        : [...prev.specializations, id],
    }));
  };

  const toggleLanguage = (id) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.includes(id)
        ? prev.languages.filter((l) => l !== id)
        : [...prev.languages, id],
    }));
  };

  const validateStep = (step) => {
    const nextErrors = {};

    if (step === 1) {
      if (!formData.phone) {
        nextErrors.phone = 'Phone number is required';
      } else if (!isValidPhoneNumber(formData.phone)) {
        nextErrors.phone = 'Please enter a valid phone number';
      }
    }

    if (accountType === 'provider' && step === 2) {
      if (!formData.businessName.trim()) nextErrors.businessName = 'Business name is required';
      if (!formData.category) nextErrors.category = 'Category is required';
    }

    if (accountType === 'provider' && step === 3) {
      if (!formData.latitude || !formData.longitude) {
        nextErrors.mapPicker = 'Please select your business location on the map';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildOnboardingPayload = () => {
    const payload = {
      onboardingToken,
      role: accountType,
      phone: formData.phone,
      googlePicture,
    };

    if (accountType === 'provider') {
      payload.providerProfile = {
        businessName: formData.businessName.trim(),
        category: formData.category,
        description: formData.description.trim(),
        location: formData.businessLocation || formData.shopAddress,
        shopAddress: formData.shopAddress,
      };
      if (googlePicture) payload.providerProfile.photoUrl = googlePicture;
    } else if (googlePicture) {
      payload.avatarUrl = googlePicture;
    }

    return payload;
  };

  const buildProviderProfilePayload = () => {
    const payload = {
      businessName: formData.businessName.trim(),
      category: formData.category,
      description: formData.description.trim(),
      location: formData.businessLocation || formData.shopAddress,
      shopAddress: formData.shopAddress,
      lat: formData.latitude,
      lng: formData.longitude,
      offersMobile: formData.offersMobile,
      autoAcceptBookings: formData.autoAcceptBookings,
      bufferTimeMinutes: parseInt(formData.bufferTimeMinutes, 10) || 15,
    };
    if (googlePicture) payload.photoUrl = googlePicture;

    if (formData.specializations.length > 0) payload.specializations = formData.specializations;
    if (formData.languages.length > 0) payload.languages = formData.languages;
    if (formData.genderPreference) payload.genderPreference = formData.genderPreference;
    if (formData.hygieneNotes.trim()) payload.hygieneNotes = formData.hygieneNotes.trim();
    if (formData.offersMobile && formData.maxTravelKm) payload.maxTravelKm = parseFloat(formData.maxTravelKm);

    return payload;
  };

  const submitOnboarding = async () => {
    setLoading(true);
    try {
      const response = await completeGoogleOnboarding(buildOnboardingPayload());
      if (accountType === 'client' && googlePicture) {
        try {
          await authAPI.updateProfile({ avatarUrl: googlePicture });
        } catch (profileError) {
          console.error('Google client avatar update error:', profileError);
        }
      }
      if (accountType === 'provider') {
        try {
          await providersAPI.updateMyProfile(buildProviderProfilePayload());
        } catch (profileError) {
          console.error('Google provider profile update error:', profileError);
          toast.error(profileError?.response?.data?.message || profileError?.response?.data?.error || 'Your account was created, but some provider details could not be saved.');
        }
      }
      navigate(resolvePostAuthDestination(from, response.user?.role), { replace: true });
    } catch (error) {
      console.error('Google onboarding error:', error?.response?.data || error);
      toast.error(error?.response?.data?.message || error?.response?.data?.error || 'We could not complete Google onboarding.');
    } finally {
      setLoading(false);
    }
  };

  const handleClientSubmit = async () => {
    if (!validateStep(1)) return;
    await submitOnboarding();
  };

  const handleNext = () => {
    if (validateStep(currentStep)) setCurrentStep((step) => step + 1);
  };

  const handleBack = () => setCurrentStep((step) => step - 1);

  const handleProviderSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;
    // Provider accounts require the plan payment before submission
    if (feeStatus !== 'COMPLETED') {
      toast.error('Please complete the plan payment first.');
      return;
    }
    await submitOnboarding();
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="w-full max-w-2xl rounded-2xl border p-6 sm:p-8" style={{ borderColor: 'var(--gray-4)', backgroundColor: '#fff' }}>
        <div className="mb-6">
          <Link to="/login" style={{ color: 'var(--gray-10)', fontSize: '14px', textDecoration: 'none' }}>
            Back to login
          </Link>
          <h1 className="mt-4 text-2xl font-bold" style={{ color: 'var(--gray-12)', fontFamily: "'Poppins', sans-serif" }}>
            Finish your Google sign-up
          </h1>
          <p className="mt-2 text-sm" style={{ color: 'var(--gray-10)' }}>
            Your Google account is connected. Choose how you want to use Salon and complete the remaining details.
          </p>
        </div>

        <div className="mb-6 rounded-xl border p-4" style={{ borderColor: 'var(--gray-4)', backgroundColor: 'var(--gray-2)' }}>
          <p className="text-sm font-medium" style={{ color: 'var(--gray-12)' }}>{profile.name || 'Google account'}</p>
          <p className="text-sm" style={{ color: 'var(--gray-10)' }}>{profile.email || 'No email returned'}</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
          {[
            { id: 'client', label: 'I am a Client', desc: 'Book beauty services', icon: UserIcon },
            { id: 'provider', label: 'I am a Provider', desc: 'Offer my services', icon: BuildingStorefrontIcon },
          ].map(({ id, label, desc, icon: Icon }) => {
            const selected = accountType === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setAccountType(id);
                  setCurrentStep(1);
                  setErrors({});
                }}
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  padding: '16px',
                  borderRadius: '12px',
                  border: selected ? '2px solid var(--orange-9)' : '1px solid var(--gray-5)',
                  backgroundColor: selected ? 'var(--orange-2)' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  backgroundColor: selected ? 'var(--orange-9)' : 'var(--gray-3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Icon style={{ width: '18px', height: '18px', color: selected ? '#fff' : 'var(--gray-10)' }} />
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)' }}>{label}</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-10)', marginTop: '2px' }}>{desc}</div>
                </div>
                {selected && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--orange-9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {accountType === 'provider' && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)' }}>
                {STEPS[currentStep - 1].title}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                Step {currentStep} of {STEPS.length}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {STEPS.map((step) => (
                <div
                  key={step.id}
                  style={{
                    flex: 1,
                    height: '4px',
                    borderRadius: '999px',
                    backgroundColor: currentStep >= step.id ? 'var(--orange-9)' : 'var(--gray-4)',
                    transition: 'background-color 0.3s ease',
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {accountType === 'client' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                Phone Number <span style={{ color: 'var(--red-9, #e5484d)' }}>*</span>
              </label>
              <PhoneInput
                international
                defaultCountry="UG"
                className="phone-input-field"
                value={formData.phone}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, phone: value || '' }));
                  if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
                }}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#ffffff' }}
              />
              {errors.phone && <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '12px', marginTop: '4px' }}>{errors.phone}</p>}
            </div>

            <div className="flex justify-end">
              <Button type="button" onClick={handleClientSubmit} loading={loading}>
                Continue
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleProviderSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {currentStep === 1 && (
              <div>
                <label style={{ display: 'block', marginBottom: '6px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                  Phone Number <span style={{ color: 'var(--red-9, #e5484d)' }}>*</span>
                </label>
                <PhoneInput
                  international
                  defaultCountry="UG"
                  className="phone-input-field"
                  value={formData.phone}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, phone: value || '' }));
                    if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
                  }}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#ffffff' }}
                />
                {errors.phone && <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '12px', marginTop: '4px' }}>{errors.phone}</p>}
              </div>
            )}

            {currentStep === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <Input
                  label="Business Name"
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  icon={<BuildingStorefrontIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                  error={errors.businessName}
                  placeholder="e.g., Elite Beauty Salon"
                  required
                />

                <Select
                  label="Business Category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  options={categories.map((cat) => ({ value: cat.id, label: cat.name }))}
                  placeholder={categories.length === 0 ? 'Loading categories...' : 'Select a category'}
                  disabled={categories.length === 0}
                  error={errors.category}
                  required
                />

                <Textarea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Tell clients about your services, experience, etc..."
                  rows={4}
                />

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                    Specializations
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    {SPECIALIZATIONS.map((spec) => (
                      <button
                        key={spec.id}
                        type="button"
                        onClick={() => toggleSpecialization(spec.id)}
                        style={{
                          padding: '12px',
                          borderRadius: '8px',
                          border: formData.specializations.includes(spec.id) ? '2px solid var(--orange-9)' : '1px solid var(--gray-5)',
                          backgroundColor: formData.specializations.includes(spec.id) ? 'var(--orange-1)' : 'transparent',
                          color: formData.specializations.includes(spec.id) ? 'var(--orange-10)' : 'var(--gray-10)',
                          fontSize: '14px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {spec.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '10px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                    Select Location on Map <span style={{ color: 'var(--red-9, #e5484d)' }}>*</span>
                  </label>
                  <MapPicker
                    onLocationSelect={handleLocationSelect}
                    initialLat={formData.latitude || undefined}
                    initialLng={formData.longitude || undefined}
                    initialAddress={formData.shopAddress}
                  />
                  {errors.mapPicker && (
                    <p style={{ marginTop: '6px', fontSize: '12px', color: 'var(--red-9, #e5484d)' }}>{errors.mapPicker}</p>
                  )}
                  {formData.latitude && formData.longitude && (
                    <p style={{ marginTop: '6px', fontSize: '12px', color: 'var(--green-10, #16a34a)' }}>
                      Location set
                    </p>
                  )}
                </div>

                <Input
                  label="Address Label"
                  type="text"
                  name="shopAddress"
                  value={formData.shopAddress}
                  onChange={handleChange}
                  icon={<MapPinIcon style={{ width: '20px', height: '20px', color: 'var(--gray-8)' }} />}
                  placeholder="e.g., Near Nakasero Market, Kampala"
                />
              </div>
            )}

            {currentStep === 4 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ padding: '16px', borderRadius: '12px', backgroundColor: 'var(--gray-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: 0 }}>I offer mobile services</h3>
                      <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '4px 0 0' }}>I can travel to clients</p>
                    </div>
                    <input
                      type="checkbox"
                      name="offersMobile"
                      checked={formData.offersMobile}
                      onChange={handleChange}
                      style={{ width: '20px', height: '20px', accentColor: 'var(--orange-9)' }}
                    />
                  </div>

                  {formData.offersMobile && (
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', fontSize: '13px', color: 'var(--gray-10)', marginBottom: '6px' }}>
                        Maximum travel distance (km)
                      </label>
                      <input
                        type="number"
                        name="maxTravelKm"
                        value={formData.maxTravelKm}
                        onChange={handleChange}
                        min={1}
                        max={100}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '2px solid #f05a35',
                          fontSize: '14px',
                          backgroundColor: '#ffffff',
                        }}
                      />
                    </div>
                  )}
                </div>

                <div style={{ padding: '16px', borderRadius: '12px', backgroundColor: 'var(--gray-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: 0 }}>Auto-accept bookings</h3>
                      <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '4px 0 0' }}>Automatically accept booking requests</p>
                    </div>
                    <input
                      type="checkbox"
                      name="autoAcceptBookings"
                      checked={formData.autoAcceptBookings}
                      onChange={handleChange}
                      style={{ width: '20px', height: '20px', accentColor: 'var(--orange-9)' }}
                    />
                  </div>
                </div>

                <Select
                  label="Buffer time between appointments"
                  name="bufferTimeMinutes"
                  value={formData.bufferTimeMinutes}
                  onChange={handleChange}
                  options={[
                    { value: 0, label: 'No buffer' },
                    { value: 5, label: '5 minutes' },
                    { value: 10, label: '10 minutes' },
                    { value: 15, label: '15 minutes' },
                    { value: 30, label: '30 minutes' },
                  ]}
                />

                <Select
                  label="Preferred client gender"
                  name="genderPreference"
                  value={formData.genderPreference}
                  onChange={handleChange}
                  options={GENDER_OPTIONS}
                />

                <div>
                  <label style={{ display: 'block', marginBottom: '10px', color: 'var(--gray-12)', fontSize: '14px', fontWeight: 500 }}>
                    Languages spoken
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.id}
                        type="button"
                        onClick={() => toggleLanguage(lang.id)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '20px',
                          border: formData.languages.includes(lang.id) ? '2px solid var(--orange-9)' : '1px solid var(--gray-5)',
                          backgroundColor: formData.languages.includes(lang.id) ? 'var(--orange-1)' : 'transparent',
                          color: formData.languages.includes(lang.id) ? 'var(--orange-10)' : 'var(--gray-10)',
                          fontSize: '13px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>

                <Textarea
                  label="Hygiene & Safety Notes"
                  name="hygieneNotes"
                  value={formData.hygieneNotes}
                  onChange={handleChange}
                  placeholder="Any hygiene practices, safety measures, or health protocols clients should know..."
                  rows={3}
                />
              </div>
            )}

            {/* ── Step 5: Plan & Payment ── */}
            {currentStep === 5 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Plan selection */}
                {!feeStatus && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {Object.entries(PLAN_INFO).map(([key, plan]) => {
                      const selected = selectedPlan === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSelectedPlan(key)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '14px 16px', borderRadius: '12px', cursor: 'pointer', textAlign: 'left',
                            border: selected ? '2px solid var(--orange-9)' : '1px solid var(--gray-5)',
                            backgroundColor: selected ? 'var(--orange-2)' : '#fff',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div>
                            <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gray-12)', margin: 0 }}>{plan.label}</p>
                            <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '2px 0 0' }}>{plan.note}</p>
                          </div>
                          <span style={{ fontSize: '15px', fontWeight: 800, color: selected ? 'var(--orange-10)' : 'var(--gray-12)' }}>
                            UGX {plan.price.toLocaleString()}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Payment method + number */}
                {!feeStatus && (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                      {[
                        { id: 'mtn', label: 'MTN MoMo' },
                        { id: 'airtel', label: 'Airtel Money' },
                        { id: 'card', label: 'Card' },
                      ].map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setPaymentMethod(id)}
                          style={{
                            padding: '10px 6px', borderRadius: '10px', cursor: 'pointer',
                            fontSize: '12px', fontWeight: 600,
                            border: paymentMethod === id ? '2px solid var(--orange-9)' : '1px solid var(--gray-5)',
                            backgroundColor: paymentMethod === id ? 'var(--orange-2)' : '#fff',
                            color: 'var(--gray-12)',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {paymentMethod !== 'card' && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', color: 'var(--gray-12)', fontSize: '13px', fontWeight: 500 }}>
                          Number to pay with
                        </label>
                        <PhoneInput
                          international
                          defaultCountry="UG"
                          className="phone-input-field"
                          value={paymentPhone}
                          onChange={(value) => {
                            setPaymentPhone(value || '');
                            if (paymentPhoneError) setPaymentPhoneError('');
                          }}
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', backgroundColor: '#ffffff' }}
                        />
                        <PhoneAccountHint phone={paymentPhone} />
                        {paymentPhoneError && (
                          <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '12px', marginTop: '4px' }}>{paymentPhoneError}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Processing */}
                {(feeStatus === 'PROCESSING' || feeStatus === 'PENDING') && (
                  <div style={{ textAlign: 'center', padding: '16px', borderRadius: '12px', background: 'var(--gray-2)' }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', margin: '0 auto 10px',
                      border: '3px solid var(--orange-9)', borderTopColor: 'transparent',
                      animation: 'spin 0.9s linear infinite',
                    }} />
                    <p style={{ fontSize: '14px', color: 'var(--gray-11)', margin: 0 }}>Waiting for payment confirmation…</p>
                    <p style={{ fontSize: '12px', color: 'var(--gray-9)', margin: '4px 0 0' }}>
                      {paymentMethod === 'card' ? 'Enter your card details on the payment page' : 'Approve the MoMo prompt on your phone'}
                    </p>
                    {paymentMethod === 'card' && checkoutUrl && (
                      <a
                        href={checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '8px',
                          marginTop: '12px', padding: '11px 22px', borderRadius: '10px',
                          background: 'linear-gradient(135deg, var(--orange-9), #f97316)',
                          color: '#fff', fontSize: '13px', fontWeight: 600, textDecoration: 'none',
                        }}
                      >
                        Open card payment page ↗
                      </a>
                    )}
                    {!feePolling && (
                      <button
                        type="button"
                        onClick={checkFeeStatusOnce}
                        disabled={loading}
                        style={{
                          display: 'block', margin: '12px auto 0', padding: '10px 20px',
                          borderRadius: '10px', border: '1.5px solid var(--orange-9)',
                          background: 'transparent', color: 'var(--orange-9)',
                          fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        Check payment status
                      </button>
                    )}
                    <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
                  </div>
                )}

                {/* Completed */}
                {feeStatus === 'COMPLETED' && (
                  <div style={{ textAlign: 'center', padding: '16px', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <CheckCircleIcon style={{ width: 32, height: 32, color: '#16a34a', margin: '0 auto 8px' }} />
                    <p style={{ fontSize: '14px', fontWeight: 600, color: '#15803d', margin: 0 }}>
                      Payment confirmed! Complete your registration below.
                    </p>
                  </div>
                )}

                {/* Failed */}
                {feeStatus === 'FAILED' && (
                  <div style={{ textAlign: 'center', padding: '12px', borderRadius: '12px', background: '#fef2f2', border: '1px solid #fecaca' }}>
                    <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>Payment failed. Please try again.</p>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '8px' }}>
              {currentStep > 1 ? (
                <Button type="button" variant="outline" onClick={handleBack}>
                  <ChevronLeftIcon style={{ width: '18px', height: '18px', marginRight: '6px' }} />
                  Back
                </Button>
              ) : (
                <div />
              )}

              {currentStep < 5 ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <ChevronRightIcon style={{ width: '18px', height: '18px', marginLeft: '6px' }} />
                </Button>
              ) : feeStatus === 'COMPLETED' ? (
                <Button type="submit" loading={loading}>
                  Complete Registration
                  <CheckCircleIcon style={{ width: '18px', height: '18px', marginLeft: '6px' }} />
                </Button>
              ) : (
                <Button type="button" onClick={handlePayFee} loading={loading || feePolling}>
                  {feeStatus === 'FAILED'
                    ? 'Retry Payment'
                    : `Pay ${PLAN_INFO[selectedPlan].label} — UGX ${PLAN_INFO[selectedPlan].price.toLocaleString()}`}
                </Button>
              )}
            </div>
          </form>
        )}

        <style>{`
          .phone-input-field {
            border: 1px solid var(--gray-6);
            transition: border-color 0.15s ease, box-shadow 0.15s ease;
          }
          .phone-input-field:focus-within {
            border-color: #f05a35;
            box-shadow: 0 0 0 2px rgba(255,120,0,0.25);
          }
          .phone-input-field .PhoneInputInput {
            border: none;
            outline: none;
            background: transparent;
            font-size: 14px;
            color: #1f1f1f;
          }
        `}</style>
      </div>
    </div>
  );
};

export default GoogleOnboarding;
